import React, { createContext, useContext, useState, useEffect } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { User } from "@supabase/supabase-js";
import { UserRole, Profile, UserWithProfile } from "@/models/user";

interface AuthContextType {
  user: UserWithProfile | null;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isSeller: boolean;
  isContabilidad: boolean;
  isMantenimiento: boolean;

  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  isLoading: boolean;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<UserWithProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const loadUserProfile = async (userId: string): Promise<Profile | null> => {
    try {
      console.log("Cargando perfil para el usuario ID:", userId);
      
      const { data: profile, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", userId)
        .single();

      if (error) {
        console.error("Error al cargar el perfil:", error);
        return null;
      }

      if (!profile) {
        console.warn("No se encontró perfil para el usuario ID:", userId);
        return null;
      }
      
      console.log("Perfil encontrado:", profile);
      
      const validRoles = ['admin', 'seller', 'contabilidad', 'mantenimiento'];
      const role = validRoles.includes(profile.role) ? profile.role as UserRole : 'seller' as UserRole;

      return {
        id: profile.id,
        full_name: profile.full_name,
        email: profile.email,
        role
      };
    } catch (err) {
      console.error("Error al cargar el perfil:", err);
      return null;
    }
  };

  const createUserProfile = async (
    userId: string, 
    email: string, 
    fullName: string | null = null, 
    role: UserRole = 'seller'
  ): Promise<Profile | null> => {
    try {
      console.log("Creando perfil para el usuario:", userId);
      
      const profileData = {
        id: userId,
        email,
        full_name: fullName,
        role
      };
      
      const { error } = await supabase
        .from('profiles')
        .insert(profileData);
      
      if (error) {
        console.error("Error al crear perfil:", error);
        return null;
      }
      
      console.log("Perfil creado exitosamente");
      return profileData as Profile;
    } catch (err) {
      console.error("Error al crear perfil:", err);
      return null;
    }
  };

  const refreshUser = async () => {
    if (!user) return;
    
    try {
      const profile = await loadUserProfile(user.id);
      
      if (profile) {
        const updatedUser = {
          ...user,
          profile: profile
        };
        
        setUser(updatedUser);
        console.log("User refreshed with profile:", profile);
      }
    } catch (error) {
      console.error("Error al refrescar el usuario:", error);
    }
  };

  useEffect(() => {
    const initAuth = async () => {
      setIsLoading(true);
      try {
        const { data: sessionData } = await supabase.auth.getSession();
        
        if (sessionData.session?.user) {
          const profile = await loadUserProfile(sessionData.session.user.id);
          
          if (profile) {
            const userWithProfile = {
              ...sessionData.session.user,
              profile: profile
            };
            
            setUser(userWithProfile);
            console.log("User initialized with profile:", profile);
          } else {
            setUser(sessionData.session.user as UserWithProfile);
            console.log("User initialized without profile");
          }
        } else {
          console.log("No active session found");
        }
      } catch (error) {
        console.error("Error al inicializar la autenticación:", error);
      } finally {
        setIsLoading(false);
      }
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (_event, session) => {
        setTimeout(async () => {
          if (session?.user) {
            try {
              setIsLoading(true);
              const profile = await loadUserProfile(session.user.id);
              
              if (profile) {
                const userWithProfile = {
                  ...session.user,
                  profile: profile as Profile
                };
                
                setUser(userWithProfile);
                console.log("Auth state change - user with profile:", profile);
              } else {
                setUser(session.user as UserWithProfile);
                console.log("Auth state change - user without profile");
              }
            } catch (error) {
              console.error("Error al actualizar el perfil del usuario:", error);
            } finally {
              setIsLoading(false);
            }
          } else {
            setUser(null);
            console.log("Auth state change - no user");
            setIsLoading(false);
          }
        }, 0);
      }
    );

    initAuth();

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const login = async (email: string, password: string) => {
    try {
      setIsLoading(true);
      console.log("Intentando iniciar sesión con email:", email);
      
      // Normalizar el correo electrónico
      const normalizedEmail = email.trim().toLowerCase();
      
      // 1. Verificar si el usuario existe en la tabla profiles
      const { data: profileData, error: profileError } = await supabase
        .from("profiles")
        .select("*")
        .eq("email", normalizedEmail)
        .single();
      
      if (profileError && !profileError.message.includes('No rows found')) {
        console.error("Error al verificar el perfil durante el login:", profileError);
      }
      
      if (profileData) {
        console.log("Perfil encontrado durante el login:", profileData);
      } else {
        console.warn("No se encontró perfil para el email:", normalizedEmail);
      }
      
      // 2. Intentar autenticar al usuario
      const { data, error } = await supabase.auth.signInWithPassword({
        email: normalizedEmail,
        password
      });
      
      if (error) {
        console.error("Error detallado de autenticación:", {
          message: error.message,
          status: error.status,
          name: error.name,
          details: error
        });
        
        // Si es un error de credenciales, intentar algo diferente
        if (error.message.includes("Invalid login credentials")) {
          // Podríamos intentar restablecer la contraseña o verificar si el usuario existe
          const { count, error: userCheckError } = await supabase
            .from('profiles')
            .select('*', { count: 'exact', head: true })
            .eq('email', normalizedEmail);
          
          if (userCheckError) {
            console.error("Error al verificar si el usuario existe:", userCheckError);
          } else if (count && count > 0) {
            // El usuario existe pero las credenciales son incorrectas
            throw new Error("La contraseña es incorrecta. Por favor, verifica tus credenciales.");
          } else {
            throw new Error("No existe una cuenta con este correo electrónico.");
          }
        }
        
        throw new Error("Credenciales inválidas");
      }

      if (!data.user) {
        console.error("No se pudo obtener información del usuario después de autenticar");
        throw new Error("Error al obtener información del usuario");
      }
      
      console.log("Usuario autenticado correctamente:", data.user.id);
      
      // 3. Cargar el perfil del usuario
      const profile = await loadUserProfile(data.user.id);
      
      if (profile) {
        console.log("Perfil cargado después de la autenticación:", profile);
        const userWithProfile = {
          ...data.user,
          profile: profile
        };
        
        setUser(userWithProfile);
        toast.success(`¡Bienvenido, ${profile?.full_name || data.user.email}!`);
      } else {
        console.warn("No se pudo cargar el perfil después de la autenticación, creando manualmente");
        
        // 4. Si no se encuentra el perfil, intentar crearlo manualmente
        let userRole: UserRole = 'seller';
        let userName: string | null = null;
        
        // Intentar obtener el rol y nombre de los metadatos del usuario
        if (data.user.user_metadata) {
          if (data.user.user_metadata.role) {
            const metadataRole = data.user.user_metadata.role as string;
            if (metadataRole === 'admin' || metadataRole === 'seller' || metadataRole === 'contabilidad' || metadataRole === 'mantenimiento') {
              userRole = metadataRole as UserRole;
            }
          }
          
          if (data.user.user_metadata.full_name) {
            userName = data.user.user_metadata.full_name as string;
          }
        }
        
        // Crear el perfil
        const newProfile = await createUserProfile(
          data.user.id, 
          normalizedEmail, 
          userName, 
          userRole
        );
        
        if (newProfile) {
          const userWithProfile = {
            ...data.user,
            profile: newProfile
          };
          
          setUser(userWithProfile);
          toast.success(`¡Bienvenido, ${newProfile?.full_name || data.user.email}!`);
        } else {
          setUser(data.user as UserWithProfile);
          toast.success(`¡Bienvenido, ${data.user.email}!`);
        }
      }
    } catch (error) {
      console.error("Error de inicio de sesión:", error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      await supabase.auth.signOut();
      setUser(null);
      toast.info("Sesión cerrada correctamente");
    } catch (error) {
      console.error("Error al cerrar sesión:", error);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isAdmin: user?.profile?.role === "admin",
        isSeller: user?.profile?.role === "seller",
        isContabilidad: user?.profile?.role === "contabilidad",
        isMantenimiento: user?.profile?.role === "mantenimiento",
    
        login,
        logout,
        isLoading,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth debe ser usado dentro de un AuthProvider");
  }
  return context;
};
